const METHOD = {
	GET: 'GET',
	HEAD: 'HEAD',
	PUT: 'PUT',
	DELETE: 'DELETE',
	PATCH: 'PATCH',
	POST: 'POST',
}

export const api = {
	root: window.ncs_api,

	call: async (url, parameters) => {
		const finalUrl = url.indexOf('http') === 0 ? url : `${api.root}${url}`
		const response = await fetch(finalUrl, parameters)

		return response
	},

	parameters: (apiKey, method = METHOD.GET, body = {}, mode = 'cors') => {
		const withBody = [METHOD.PUT, METHOD.PATCH, METHOD.POST]

		const apiRoot = window.ncs_api || ''
		const lastSlashIdx = apiRoot.lastIndexOf('/')
		const ncsApiEnv =
			lastSlashIdx === -1 ? apiRoot : apiRoot.slice(lastSlashIdx + 1)
			
		const params = {
			method,
			headers: {
				'X-Api-Key': apiKey,
				'Content-Type': 'application/json',
			},
			credentials: ncsApiEnv === 'LOCAL' ? 'include' : undefined,
			json: true,
		}

		if (withBody.indexOf(method) !== -1) {
			params.body = JSON.stringify(body)
			if (method === METHOD.PUT) {
				params.headers['Content-Length'] = 0
			}
		}

		return params
	},

	get: async (url, api_key) => {
		const response = await api.call(url, api.parameters(api_key))
		return response.json()
	},

	getJson: async (url, api_key) => {
		const response = await api.call(url, api.parameters(api_key))
		return response.json()
	},

	post: async (url, body = {}, api_key) => {
		const response = await api.call(
			url,
			api.parameters(api_key, METHOD.POST, body),
		)
		return response.json()
	},

	delete: async (url, api_key) => {
		const response = await api.call(url, api.parameters(api_key, METHOD.DELETE))
		return response.json()
	},

	putFile: async (url, body = {}, formData = false, api_key) => {
		const response = await api.call(
			url,
			api.parameters(api_key, METHOD.PUT, body, 'cors', formData),
		)
		return response
	},

	patch: async (url, body = {}, api_key) => {
		const response = await api.call(
			url,
			api.parameters(api_key, METHOD.PATCH, body),
		)
		return response.json()
	},

	/**
	 * POST request with X-HTTP-Method-Override: GET header
	 * Used for endpoints that need to receive large payloads but are designed as GET endpoints
	 */
	postOverrideGet: async (url, body = {}, api_key) => {
		const finalUrl = url.indexOf('http') === 0 ? url : `${api.root}${url}`
		
		const apiRoot = window.ncs_api || ''
		const lastSlashIdx = apiRoot.lastIndexOf('/')
		const ncsApiEnv =
			lastSlashIdx === -1 ? apiRoot : apiRoot.slice(lastSlashIdx + 1)
		
		const response = await fetch(finalUrl, {
			method: METHOD.POST,
			headers: {
				'X-Api-Key': api_key,
				'Content-Type': 'application/json',
				'X-HTTP-Method-Override': 'GET',
			},
			credentials: 'LOCAL' === ncsApiEnv ? "include" : undefined,
			body: JSON.stringify(body),
		})
		return response.json()
	},
}

/* ***** Contact API calls ***** */

export const getContacts = (div_id, api_key) => {
	return api.get(`/ncs/client/2/contacts/json?DIV_ID=${div_id}`, api_key)
}

export const getContact = (cnt_id, api_key) => {
	return api.get(`/cli/jobinformation/1/contact/${cnt_id}/json`, api_key)
}

/* ***** Division API calls ***** */

export const getLocations = (api_key) => {  
  return api.get(`/ncs/client/2/divisions/json`, api_key)
}

export const getLocationsByCNTID = (cnt_id, api_key) => {
  return api.get(`/ncs/client/2/divisions/json?CNT_ID=${cnt_id}`, api_key)
}

/*
export const getLocation = (div_id, api_key) => {
  return api.get(`/ncs/client/2/divisions/json?DIV_ID=${div_id}`, api_key)
}
*/

/* ***** Associate Search API calls ***** */

export const searchAssociates = (searchTerm, api_key) => {
	const body = {
		search: searchTerm,
		low: 0,
		high: 250,
		max_size: 15,
	}
	return api.postOverrideGet('/ols/nml/1/search_assoc/json', body, api_key)
}

/**
 * OLS duplicate check for customer reference number.
 * POST `/duplicateCheck` with body `{ reference_no }` on `window.ncs_api`.
 * @param {string} api_key - Must be the company's default contact api_key.
 * @returns {Promise<boolean>} true if the reference is already used (duplicate).
 */
export async function checkOlsReferenceNoDuplicate(referenceNo, api_key) {
	const ref = (referenceNo || '').trim()
	if (!ref) {
		return false
	}
	if (!api_key) {
		const err = new Error('Could not verify reference number with OLS.')
		err.code = 'REFERENCE_CHECK_UNAVAILABLE'
		throw err
	}
	const response = await api.call(
		'/cli/jobinformation/1/duplicateCheck',
		api.parameters(api_key, METHOD.POST, { reference_no: ref }),
	)
	const data = await response.json().catch(() => ({}))
	if (!response.ok) {
		const err = new Error(data.message || data.error || 'OLS duplicate check failed')
		err.body = data
		err.status = response.status
		throw err
	}
	if (data && typeof data === 'object') {
		if (
			Array.isArray(data?.interProjMatches) &&
			data?.interProjMatches?.length > 0
		) {
			return true
		}
	}
	return false
}

export const getAssociate = (assoc_id, api_key) => {
	return api.get(`/ols/nml/1/assoc/${assoc_id}/json`, api_key)
}
